import CustomButton from 'Components/CustomButton'
import DeleteButton from 'Components/DeleteButton'
import DrawerRight from 'Components/DrawerRight'
import { WithLoader } from 'Components/WithLoader'
import {
  DOMICILE_CLASS_DRAWER_TAB_PARAM_KEY,
  PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY
} from 'Services/ProductManagement/constants'
import { useGetDomicileClassParameterGroupQuery } from 'Services/ProductManagement/Features/Parameter/parameterApi'
import ProductAvailability from 'Services/ProductManagement/Features/Product/Components/ProductAvailability'
import ProductCatalogs from 'Services/ProductManagement/Features/Product/Components/ProductCatalogs'
import { ProductDetails } from 'Services/ProductManagement/Features/Product/Components/ProductDetails'
import ProductPricing from 'Services/ProductManagement/Features/Product/Components/ProductPricing'
import { SELECTED_PRODUCT_PARAMETER_KEY } from 'Services/ProductManagement/Features/Product/Hooks/useProductsWithParams'
import { useGetSingleProductQuery } from 'Services/ProductManagement/Features/Product/productApi'
import { PricingTabType } from 'Services/ProductManagement/Features/Product/types'
import TariffProductDeleteDialog from 'Services/ProductManagement/Features/Tariff/Components/TariffProductDeleteDialog'
import { useSelectedTariff } from 'Services/ProductManagement/Features/Tariff/Hooks'
import { TariffProductTabType } from 'Services/ProductManagement/Features/Tariff/types'
import { useGetSingleVatDefinitionQuery } from 'Services/ProductManagement/Features/VATDefinition/vatDefinitionApi'
import { GridHighlightProvider } from 'Util/Context/gridHighlightContext'
import { usePreviousLocation } from 'Util/Context/previousLocationContext'
import { useUnsavedChanges } from 'Util/Context/unsavedChangesContext'
import {
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_TARIFF_EDIT_ALLOWED,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PRODUCT_MANAGEMENT_ROOT_URL
} from 'Util/constants'
import EditIcon from '@mui/icons-material/Edit'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Box, Divider, Tab } from '@mui/material'
import { useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

interface TariffProductDrawerProps {
  open: boolean
  handleClose: () => void
}

export function TariffProductDrawer({ open, handleClose }: Readonly<TariffProductDrawerProps>) {
  const baseUrl = `${PRODUCT_MANAGEMENT_ROOT_URL}/tariffs`
  const navigate = useNavigate()
  const { id: tariffId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const { singleTariff } = useSelectedTariff(tariffId)
  const { confirmForUnsavedChanges } = useUnsavedChanges()
  const [isDeleteProductDialogOpen, setIsDeleteProductDialogOpen] = useState(false)

  const { data: vatDefinition = null, isLoading: isVatDefinitionsLoading } =
    useGetSingleVatDefinitionQuery(singleTariff?.vat_definition_id ?? '', {
      skip: !open || !singleTariff?.vat_definition_id
    })

  const {
    data: domicileClassParameterGroup = null,
    isLoading: isDomicileClassParameterGroupLoading
  } = useGetDomicileClassParameterGroupQuery(undefined, {
    skip: !open || IS_WALTTI_SERVICE_ENVIRONMENT // Waltti does not use domicile class based pricing
  })

  const productDetailsDrawerTabFromParams =
    searchParams.get(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY) ?? TariffProductTabType.DETAILS

  const selectedProductId = searchParams.get(SELECTED_PRODUCT_PARAMETER_KEY)
  const { data: singleProduct, isFetching: isSingleProductFetching } = useGetSingleProductQuery(
    selectedProductId ?? '',
    {
      skip: !selectedProductId
    }
  )

  const previousLocation = usePreviousLocation(
    `${baseUrl}?${SELECTED_PRODUCT_PARAMETER_KEY}=${singleProduct?.id}`
  )

  const handleProductDetailsDrawerTabChange = (
    _event: React.SyntheticEvent,
    newValue: TariffProductTabType
  ) => {
    confirmForUnsavedChanges(() => {
      searchParams.set(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY, newValue)
      if (newValue !== TariffProductTabType.PRICING) {
        searchParams.delete(DOMICILE_CLASS_DRAWER_TAB_PARAM_KEY)
      }
      setSearchParams(searchParams)
    })
  }

  const handleDrawerClose = () => {
    confirmForUnsavedChanges(() => {
      searchParams.delete(PRODUCT_DETAILS_DRAWER_TAB_PARAM_KEY)
      setSearchParams(searchParams)
      setIsDeleteProductDialogOpen(false)
      handleClose()
    })
  }

  /**
   * In Waltti environment only Exchange period is shown on tariff product level.
   * HSL environment has only Pricing enabled.
   */
  const allowedPricingTabs = IS_WALTTI_SERVICE_ENVIRONMENT
    ? [PricingTabType.EXCHANGE_PERIOD]
    : [PricingTabType.PRICINGS]

  const showProductDeleteButton = singleProduct && IS_TARIFF_EDIT_ALLOWED
  const showProductEditButton = singleProduct && IS_TARIFF_EDIT_ALLOWED

  return (
    <DrawerRight
      open={open}
      onClose={handleDrawerClose}
      topBar={
        <Box>
          {showProductDeleteButton && (
            <>
              <DeleteButton
                sx={{ mr: 1 }}
                size="small"
                onClick={() => setIsDeleteProductDialogOpen(true)}
              >{t`Delete`}</DeleteButton>
              <TariffProductDeleteDialog
                open={isDeleteProductDialogOpen}
                handleClose={() => setIsDeleteProductDialogOpen(false)}
                singleProduct={singleProduct}
                tariffId={tariffId}
                onDeleteSuccess={handleDrawerClose}
              />
            </>
          )}
          {showProductEditButton && (
            <CustomButton
              onClick={() => navigate(`${baseUrl}/${tariffId}/products/${singleProduct.id}/edit`)}
              size="small"
              startIcon={<EditIcon />}
            >{t`Edit`}</CustomButton>
          )}
        </Box>
      }
    >
      <TabContext value={productDetailsDrawerTabFromParams}>
        <TabList
          onChange={handleProductDetailsDrawerTabChange}
          aria-label={t`Tariff product tab list`}
        >
          <Tab label={t`Product details`} value={TariffProductTabType.DETAILS} />
          <Tab
            label={IS_HSL_SERVICE_ENVIRONMENT ? t`Pricing` : t`Exchange period`}
            value={TariffProductTabType.PRICING}
          />
          <Tab
            label={t`Product's sales channels`}
            value={TariffProductTabType.SALES_CHANNELS}
            sx={{ display: IS_HSL_SERVICE_ENVIRONMENT ? 'flex' : 'none' }}
          />
          <Tab label={t`Availability`} value={TariffProductTabType.AVAILABILITY} />
        </TabList>
        <Divider />
        <TabPanel sx={{ p: 0 }} value={TariffProductTabType.DETAILS}>
          <WithLoader loading={isSingleProductFetching}>
            {singleProduct && (
              <ProductDetails
                product={singleProduct}
                vatDefinition={vatDefinition}
                isVatDefinitionsLoading={isVatDefinitionsLoading}
              />
            )}
          </WithLoader>
        </TabPanel>
        <TabPanel sx={{ p: 0 }} value={TariffProductTabType.PRICING}>
          <WithLoader loading={isSingleProductFetching || isDomicileClassParameterGroupLoading}>
            {singleProduct && (
              <GridHighlightProvider>
                <ProductPricing
                  key={singleProduct.id}
                  product={singleProduct}
                  domicileClassParameterGroup={domicileClassParameterGroup}
                  allowedTabs={allowedPricingTabs}
                />
              </GridHighlightProvider>
            )}
          </WithLoader>
        </TabPanel>
        {IS_HSL_SERVICE_ENVIRONMENT && (
          <TabPanel sx={{ p: 0 }} value={TariffProductTabType.SALES_CHANNELS}>
            <WithLoader loading={isSingleProductFetching}>
              {singleProduct && (
                <ProductCatalogs product={singleProduct} handleClose={handleClose} />
              )}
            </WithLoader>
          </TabPanel>
        )}
        <TabPanel sx={{ p: 0 }} value={TariffProductTabType.AVAILABILITY}>
          <WithLoader loading={isSingleProductFetching}>
            {singleProduct && (
              <ProductAvailability
                product={singleProduct}
                successBaseUrl={previousLocation}
                handleClose={handleDrawerClose}
              />
            )}
          </WithLoader>
        </TabPanel>
      </TabContext>
    </DrawerRight>
  )
}
